import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Volume2, Square, Loader2, Trophy, Award } from "lucide-react";
import { api, money, USER_ID, track } from "./api";
import { useFlags } from "./flags";
import CinematicTrailer from "./CinematicTrailer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AttractScreen({ open, onDismiss }) {
  const flags = useFlags();
  const navigate = useNavigate();
  const [dishes, setDishes] = useState([]);
  const [clip, setClip] = useState(null);
  const [champ, setChamp] = useState(null);
  const [cines, setCines] = useState([]);
  const [shouts, setShouts] = useState([]);
  const [shoutIdx, setShoutIdx] = useState(0);
  const [idx, setIdx] = useState(0);
  const [voice, setVoice] = useState("idle");

  const stopHook = useCallback(() => {
    window.__xpAttractAudio?.pause();
    window.__xpAttractAudio = null;
    setVoice("idle");
  }, []);

  useEffect(() => {
    if (!open) stopHook();
  }, [open, stopHook]);

  useEffect(() => {
    if (open && dishes.length === 0) {
      api.get("/dishes", { params: { featured: true } }).then((r) => setDishes(r.data.slice(0, 10)));
      api.get("/attract/clip").then((r) => setClip(r.data.clip)).catch(() => {});
      api.get("/arcade/champion").then((r) => setChamp(r.data.champion)).catch(() => {});
      api.get("/arcade/recent-badges").then((r) => setShouts(r.data.recent || [])).catch(() => {});
      api.get("/cinematic").then((r) => setCines(r.data.experiences || [])).catch(() => {});
    }
  }, [open, dishes.length]);

  useEffect(() => {
    if (!open || shouts.length < 2) return;
    const t = setInterval(() => setShoutIdx((i) => (i + 1) % shouts.length), 5000);
    return () => clearInterval(t);
  }, [open, shouts.length]);

  const trailers = cines.map((c) => ({ type: "trailer", slug: `trailer-${c.slug}`, name: c.headline, price: c.price, tagline: c.sub, exp: c }));
  const mix = [];
  let ti = 0;
  dishes.forEach((d, i) => {
    if (i % 3 === 0 && ti < trailers.length) mix.push(trailers[ti++]);
    mix.push(d);
  });
  while (ti < trailers.length) mix.push(trailers[ti++]);
  const slides = clip ? [{ type: "clip", ...clip }, ...mix] : mix;

  useEffect(() => {
    if (!open || slides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 7000);
    return () => clearInterval(t);
  }, [open, slides.length]);

  const playHook = async (e) => {
    e.stopPropagation();
    if (voice === "playing") return stopHook();
    setVoice("loading");
    try {
      track("attract_hook_tapped", {});
      const r = await api.post("/attract/speak", { user_id: USER_ID });
      if (!r.data.audio_url) return setVoice("idle");
      const audio = new Audio(`${BACKEND_URL}${r.data.audio_url}`);
      window.__xpAttractAudio = audio;
      audio.onended = () => setVoice("idle");
      await audio.play();
      setVoice("playing");
    } catch {
      setVoice("idle");
    }
  };

  if (!open || slides.length === 0) return null;
  const dish = slides[idx % slides.length];
  const isClip = dish.type === "clip";
  const isTrailer = dish.type === "trailer";

  return (
    <div
      data-testid="attract-screen"
      onPointerDown={() => { onDismiss(); if (isTrailer) { track("attract_trailer_tapped", { slug: dish.exp.slug }); navigate(`/experience/cinematic/${dish.exp.slug}`); } }}
      className="fixed inset-0 z-[90] bg-[#050505] cursor-pointer overflow-hidden"
      role="button"
      aria-label={isTrailer ? "Tap to step inside the cinematic experience" : "Tap to start exploring"}
    >
      {isClip ? (
        <video key="attract-clip" data-testid="attract-clip-video" src={dish.video_url.startsWith("http") ? dish.video_url : `${BACKEND_URL}${dish.video_url}`}
          poster={dish.image || undefined}
          className="absolute inset-0 w-full h-full object-cover attract-kenburns"
          autoPlay loop muted playsInline onError={() => setClip(null)} />
      ) : isTrailer ? (
        <CinematicTrailer key={dish.slug} exp={dish.exp} />
      ) : (
        <img key={dish.slug} src={dish.image} alt={dish.name} className="absolute inset-0 w-full h-full object-cover attract-kenburns" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-black/40" />
      <div className="absolute inset-0 flex flex-col items-center justify-between py-14 px-6 text-center">
        <div>
          <p className="text-[11px] tracking-[0.45em] text-[#D4AF37]">URBAN BY VEERGI</p>
          <p className="font-display text-lg italic text-white/70 mt-1">The World of Punjabi Food</p>
          {champ && (
            <div data-testid="attract-champion" className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#E25822]/60 bg-black/55 backdrop-blur-md px-5 py-2.5">
              <Trophy size={14} className="text-[#D4AF37] shrink-0" />
              <span className="text-[11px] font-bold tracking-wider text-white/85">
                STREET FOOD RUN CHAMPION — <span className="text-[#D4AF37] font-black">{champ.nickname} · {champ.score}</span> — CAN YOU BEAT IT?
              </span>
            </div>
          )}
          {shouts.length > 0 && (
            <div>
              <div data-testid="attract-badge-shout" className="attract-fade mt-2 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 bg-black/55 backdrop-blur-md px-5 py-2.5" key={`bs-${shoutIdx}`}>
                <Award size={14} className="text-[#D4AF37] shrink-0" />
                <span className="text-[11px] font-bold tracking-wider text-white/85">
                  <span className="text-[#D4AF37] font-black">{shouts[shoutIdx].who}</span> JUST EARNED <span className="text-[#D4AF37] font-black">{shouts[shoutIdx].badge.toUpperCase()}</span> · {shouts[shoutIdx].pa} — AT THE MELA
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="attract-fade" key={`t-${dish.slug}`}>
          {isClip && <p className="text-[10px] tracking-[0.4em] text-[#E25822] font-black mb-2">FRESH FROM THE KITCHEN — WATCH</p>}
          {isTrailer && <p data-testid="attract-trailer-kicker" className="text-[10px] tracking-[0.4em] text-[#D4AF37] font-black mb-2">A CINEMATIC EXPERIENCE — TAP TO STEP INSIDE</p>}
          <h2 className="font-display text-5xl font-bold tracking-tighter leading-none">{dish.name}</h2>
          {!isClip && (
            <p className="text-base text-white/75 italic mt-3 max-w-md mx-auto" data-testid="attract-tagline">
              {dish.tagline || dish.description}
            </p>
          )}
          <p className="text-2xl font-black text-[#D4AF37] mt-3">{money(dish.price)}</p>
        </div>
        <div>
          {flags?.voice_ready === true && (
            <button
              data-testid="attract-voice-btn"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={playHook}
              className="mb-4 inline-flex items-center gap-2 bg-[#D4AF37] text-black rounded-full px-6 py-3 text-[11px] font-bold tracking-[0.25em]"
            >
              {voice === "loading" ? <Loader2 size={14} className="animate-spin" /> : voice === "playing" ? <Square size={12} /> : <Volume2 size={14} />}
              {voice === "loading" ? "IK MINT…" : voice === "playing" ? "STOP" : "HEAR VEERGI"}
            </button>
          )}
          <div>
            <span className="inline-block border border-[#D4AF37] text-[#D4AF37] rounded-full px-8 py-3.5 text-sm font-bold tracking-[0.3em] animate-pulse">
              TAP TO EXPLORE
            </span>
          </div>
          <div className="flex gap-1.5 justify-center mt-6">
            {slides.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx % slides.length ? "bg-[#D4AF37]" : "bg-white/25"}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
